

















































































import _ from 'lodash'
import Vue from 'vue'
import Translatable, {Translation} from '@/modules/i18n/model/Translatable'
import {Prop} from 'vue-property-decorator'
import Component from 'vue-class-component'
import {Getter, State} from 'vuex-class'
import Locale from '@/modules/i18n/model/Locale'
import LocaleSelect from '@/modules/i18n/components/LocaleSelect.vue'
import {Config} from '@/modules/core/config';

/**
 * Selector de traducciones
 *
 * Este componente genérico permite mostrar al usuario las traducciones de una
 * determinada entidad, además de añadir nuevas y seleccionar una.
 *
 * @author Alejandro G. Riaño <agutierrez@softcontrol.es>
 */
@Component({
    components: {LocaleSelect},
})
export default class TranslationSelect extends Vue {
    /**
     * Entidad traducible almacenada en el estado de vuex
     *
     * La propiedad vuexEntity indica al componente donde se encuentra, en el estado de
     * la aplicación, la entidad Translatable de la cual el componente mostrará
     * las traducciones registradas.
     *
     * @property string
     */
    @Prop({type: String, required: true})
    public vuexEntity: string

    /**
     * Esta propiedad indica al componente donde buscar, en el estado de la
     * aplicación (vuex), la traducción que se encuentra actualmente
     * seleccionada
     */
    @Prop({type: String, required: true})
    public vuexCurrentTranslation: string

    /**
     * Nombre de la mutación encargada de registrar un nueva traducción en la
     * entidad actual
     */
    @Prop({type: String, required: true})
    public vuexAddTranslationMutation: string

    /**
     * Nombre de la mutación del estado de vuex encargada de eliminar una traducción
     */
    @Prop({type: String, required: true})
    public vuexDeleteTranslationMutation: string

    /**
     * Nombre de la mutación de vuex encargada de establecer una traducción como
     */
    @Prop({type: String, required: true})
    public vuexSetCurrentTranslationMutation: string

    /**
     * Localismo por defecto configurado en la aplicación
     */
    @State(state => state.i18n.defaultLocale)
    public defaultLocale: Locale

    /**
     * Configuración del sistema
     */
    @State('config')
    public config: Config

    /**
     * Getter de vuex que permite obtener el nombre de un idioma en
     * base a su identificador (locale)
     *
     * Es usado por el componente para mostrar los nombres de los
     * idiomas en la lista de traducciones
     */
    @Getter('i18n/worldLocaleName')
    public localeName: (identifier: string) => string

    /**
     * Selección de localismo
     *
     * Controla la visibilidad del dialogo que permite al usuario añadir una nueva
     * traducción seleccionando un localismo de la lista.
     */
    public localeSelection = false

    public deleteConfirm = false
    public loading = true

    public currentLocale: Locale | string = ''

    /**
     * El componente no será mostrado si en la configuración del panel se encuentra
     * activo el modo "Único lenguaje".
     */
    public get canDisplayTranslations () {
        return !this.config.get('i18n.singleLanguage', false)
    }

    /**
     * Filtrado de localismo inyectado al componente LocaleSelect
     */
    public localeFilter = (locale: Locale[]) => {
        return locale.filter(locale => {
            return this.entityLocales.findIndex(l => l === locale.identifier) === -1
        })
    }

    /**
     * Retorna la lista de localismo registrados en las traducciones de la entidad
     * seleccionada por el usuario
     */
    public get entityLocales (): string[] {
        return this.entity.translations.map(translation => {
            return typeof translation.locale === 'string' ? translation.locale : translation.locale.identifier
        })
    }

    /**
     * Comprueba si el localismo se puede eliminar
     */
    public canDeleteLocale (locale: string): boolean {
        return locale !== this.defaultLocale.identifier
    }

    /**
     * Permite acceder a la entidad que el usuario ha seleccionado y esta editando
     * actualmente.
     */
    public get entity (): Translatable {
        if (!this.vuexEntity) {
            throw new Error('vuexEntity no puede estar vacío')
        }

        const entity =  _.get(this.$store.state, this.vuexEntity)
        if (!entity) {
            throw new Error(`No existe ninguna entidad en ${this.vuexEntity}`)
        }

        return entity
    }

    /**
     * Retorna el identificador ISO de la traducción actual
     */
    public get currentTranslationLocale (): string | null {
        if (!this.vuexCurrentTranslation) {
            throw new Error('La ruta del estado que apunta a la traducción no puede estar vacía')
        }

        const translation: Translation | string | null = _.get(this.$store.state, this.vuexCurrentTranslation, null)
        if (!translation) {
            return null
        }

        if (typeof translation === 'string') {
            return translation
        }

        return typeof translation.locale === 'string'
            ? translation.locale
            : translation.locale.identifier
    }

    /**
     * Añade una nueva traducción a la entidad
     *
     * @param locale
     */
    public addTranslation (locale: Locale) {
        if (!this.vuexAddTranslationMutation) {
            throw new Error('No se ha definido ninguna mutación para añadir traducciones')
        }

        this.$store.commit(this.vuexAddTranslationMutation, locale.identifier)
    }

    /**
     * Elimina una traducción en la entidad que se encuentra seleccionada
     * actualmente
     *
     * @param locale
     */
    public deleteTranslation (locale: string) {
        if (!this.vuexDeleteTranslationMutation) {
            throw new Error('No se ha definido ninguna mutación para añadir traducciones')
        }

        this.$store.commit(this.vuexDeleteTranslationMutation, locale)
    }

    /**
     * Al crear el componente carga el localismo por defecto configurado en la aplicación
     */
    public async created () {
        await this.$store.dispatch('i18n/loadDefaultLocale')
        this.loading = false
    }

    /**
     * Obtiene el código de país del identificador de un localismo
     * @param locale
     */
    public countryCode (locale: string): string {
        return locale.includes('_') ? locale.split('_')[1] : locale
    }

    /**
     * Establece la traducción actualmente mostrada al usuario
     *
     * @param locale
     */
    public setCurrentTranslation (locale: string) {
        if (!this.vuexSetCurrentTranslationMutation) {
            throw new Error('No se ha definido la mutación de Vuex para establecer la traducción actual')
        }

        this.$store.commit(this.vuexSetCurrentTranslationMutation, locale)
    }
}
